// ═══ 화면 선택(검차 화면 ↔ 차량관리 화면) 저장·판정 — 앱 simple_mode_controller.dart 1:1 (신고 371) ═══
//
//   업체 플래그: user.simpleMode (서버 companies.simple_mode, JWT 미포함 → 로그인/switch 응답에만 있다).
//               관리자 웹의 체크는 **시작 지점(기본값)** 일 뿐이다.
//   사용자 선택: localStorage (브라우저·계정별)
//               'screen_mode_choice_<user.id>'      = 'simple' | 'full'
//               'screen_mode_choice_base_<user.id>' = 선택 당시의 플래그 ('0'|'1')
//               로드 시 base 가 현재 플래그와 다르면 두 키를 지우고 선택 폐기(관리자가 체크를 바꾼 경우).
//
// 플래그는 캐시하지 않는다 — 회사 전환은 sessionStorage.autodoc_user 를 먼저 갈아끼운다(BUILD_PLAN §6).
import { useCallback, useEffect, useState } from 'react';

// 전용 셸을 가진 role 은 제외 — 앱(cancel_agency·doc_agency)과 같은 정의 + 웹 전용 포털 role.
var EXCLUDED_ROLES = ['cancel_agency', 'doc_agency', 'driver', 'shoring', 'carrier', 'customs_broker', '관세사',
  // operator(운영 관리자)도 전용 셸이다. 빠뜨리면 '검차 화면으로 보기' 버튼이 노출돼
  // Dashboard 의 operator 라우트 분기를 통째로 우회한다 (2026-09-29 검증 지적).
  'operator'];

export function isSimpleRoleAllowed(role) {
  return EXCLUDED_ROLES.indexOf(role || '') < 0;
}

function idOf(user) { return user && user.id != null ? String(user.id) : ''; }
function choiceKey(user) { return 'screen_mode_choice_' + idOf(user); }
function baseKey(user) { return 'screen_mode_choice_base_' + idOf(user); }

/// 현재 업체 플래그(0|1). sessionStorage 의 user 가 진실 — prop 은 폴백.
export function simpleModeOf(user) {
  try {
    var raw = window.sessionStorage.getItem('autodoc_user');
    if (raw) {
      var saved = JSON.parse(raw);
      if (saved && (!user || String(saved.id) === idOf(user))) return saved.simpleMode === 1 ? 1 : 0;
    }
  } catch (e) { /* private mode / 파싱 실패 → prop 폴백 */ }
  return user && user.simpleMode === 1 ? 1 : 0;
}

/// 저장된 선택. base 불일치면 두 키를 지우고 'none'.
export function loadChoice(user) {
  if (!idOf(user)) return 'none';
  try {
    var saved = window.localStorage.getItem(choiceKey(user));
    if (saved !== 'simple' && saved !== 'full') return 'none';
    var base = window.localStorage.getItem(baseKey(user));
    if (base !== String(simpleModeOf(user))) {
      window.localStorage.removeItem(choiceKey(user));
      window.localStorage.removeItem(baseKey(user));
      return 'none';
    }
    return saved;
  } catch (e) { return 'none'; }
}

// 선택 변경 알림 — 셸(Dashboard 래퍼)과 전환 버튼(BackToSimple)이 서로 다른 훅 인스턴스를 쓰므로
// 저장만으로는 상대가 모른다. 같은 창 안의 모든 useScreenMode 가 이 이벤트로 다시 읽는다.
var CHANGE_EVENT = 'autodex:screen-mode';

export function setChoice(user, choice) {
  if (!idOf(user)) return;
  try {
    window.localStorage.setItem(choiceKey(user), choice);
    window.localStorage.setItem(baseKey(user), String(simpleModeOf(user)));
  } catch (e) { /* private mode — 세션 한정 적용 */ }
  try { window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { id: idOf(user), choice: choice } })); } catch (e) { /* 구형 브라우저 */ }
}

export function clearChoice(user) {
  if (!idOf(user)) return;
  try {
    window.localStorage.removeItem(choiceKey(user));
    window.localStorage.removeItem(baseKey(user));
  } catch (e) { /* noop */ }
}

/// 선택 + 업체 플래그 → 검차 셸 여부 (단일 정의).
export function simpleActiveFor(choice, simpleMode) {
  if (choice === 'simple') return true;
  if (choice === 'full') return false;
  return !!simpleMode;
}

/// 차량관리 셸에서 "검차 화면으로 보기" 를 노출할 조건.
// 검차 화면 진입 버튼 노출 조건 — 허용 role 이면 업체 플래그(simple_mode)와 무관하게 제공한다
// (Jin 2026-09-23: 플래그 없는 계정도 전환해서 볼 수 있어야 한다. 기본 랜딩은 플래그가 정한다).
// 관리자는 업체를 골라 들어온 상태(activeCompanyId>0)에서만 — 전체보기(0)는 검차 목록·정산이 업체 단위라 의미가 없다.
var ADMIN_ROLES = ['admin', '관리자'];
export function canOfferSimple(user) {
  if (!user || !isSimpleRoleAllowed(user.role)) return false;
  if (ADMIN_ROLES.indexOf(user.role) >= 0) return Number(user.activeCompanyId) > 0;
  return true;
}

// ═══ 말소 업체 '오토덱스 전체 기능' 둘러보기 (simplify §7) — 이 브라우저·계정에서만, 서버 플래그 무변경 ═══
//   localStorage 'malso_preview_<user.id>' = '1' (돌아가기·서버 전환 때 지운다). 검차 선택(choice)과 따로 둔다 —
//   옛 'full' 선택이 남은 말소 업체가 저절로 둘러보기로 빠지지 않게. 의미는 말소 셸 사용자(malsoActiveFor)일 때만.
function previewKey(user) { return 'malso_preview_' + idOf(user); }

export function loadMalsoPreview(user) {
  if (!idOf(user)) return false;
  try { return window.localStorage.getItem(previewKey(user)) === '1'; } catch (e) { return false; }
}

export function setMalsoPreview(user, on) {
  if (!idOf(user)) return;
  try {
    if (on) window.localStorage.setItem(previewKey(user), '1');
    else window.localStorage.removeItem(previewKey(user));
  } catch (e) { /* private mode — 이번 화면 한정 */ }
  try { window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { id: idOf(user), preview: !!on } })); } catch (e) { /* 구형 브라우저 */ }
}

/// 셸 판정 훅 — { choice, simpleActive, setChoice, malsoPreview }. 회사 전환(플래그 변동)마다 재평가한다.
export function useScreenMode(user) {
  var uid = idOf(user);
  var flag = simpleModeOf(user);
  var st = useState(function () { return loadChoice(user); });
  var choice = st[0], setChoiceState = st[1];
  var stP = useState(function () { return loadMalsoPreview(user); });
  var preview = stP[0], setPreviewState = stP[1];

  useEffect(function () {
    var sync = function () { setChoiceState(loadChoice(user)); setPreviewState(loadMalsoPreview(user)); };
    sync();
    window.addEventListener(CHANGE_EVENT, sync);
    return function () { window.removeEventListener(CHANGE_EVENT, sync); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, flag]);

  var choose = useCallback(function (next) {
    setChoice(user, next);
    setChoiceState(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, flag]);

  return {
    choice: choice,
    simpleActive: isSimpleRoleAllowed(user && user.role) && simpleActiveFor(choice, flag),
    setChoice: choose,
    malsoPreview: preview && malsoActiveFor(user)
  };
}

// ═══ 말소 화면 모드 (plan.md §3·§5, B3 리뷰 C1) — 앱 simple_mode_controller.dart malsoActiveFor 미러 ═══
//   업체 플래그 user.malsoMode (companies.malso_mode) 단독. 기기 선택 없음 — 대표 전환은 서버 PUT.
//   말소 > 검차 우선(Dashboard 래퍼가 malsoActiveFor 를 먼저 본다). 관리자(admin·관리자)는 제외 —
//   말소 셸에 업체 전환기가 없어 갇히고, 서버가 admin 을 owner 로 취급해 "전체 화면으로 전환" 이
//   그 업체의 플래그를 실제로 꺼 버린다(Jin 10/23 결정 ⑥).

/// 말소 화면 플래그(0|1) — simpleModeOf 미러(sessionStorage 우선, prop 폴백).
export function malsoModeOf(user) {
  try {
    var raw = window.sessionStorage.getItem('autodoc_user');
    if (raw) {
      var saved = JSON.parse(raw);
      if (saved && (!user || String(saved.id) === idOf(user))) return saved.malsoMode === 1 ? 1 : 0;
    }
  } catch (e) { /* private mode / 파싱 실패 → prop 폴백 */ }
  return user && user.malsoMode === 1 ? 1 : 0;
}

/// 말소 셸 활성 — 서버 플래그 단독. 전용 셸 role 제외 + 관리자 제외(C1).
export function malsoActiveFor(user) {
  return !!user && isSimpleRoleAllowed(user.role) && ADMIN_ROLES.indexOf(user.role) < 0 && malsoModeOf(user) === 1;
}

/// 전환 행 노출(말소↔전체) — 대표(currentRole 'owner')만, 관리자·전용 role 제외 (plan §5).
export function canOfferMalso(user) {
  return !!user && isSimpleRoleAllowed(user.role) && ADMIN_ROLES.indexOf(user.role) < 0 && user.currentRole === 'owner';
}
